import { currentLocale, i18n } from '../i18n'

// The editable subset of an RFC 5545 RRULE. Parts the editor doesn't understand are kept in
// `extra` and written back unchanged, so rules made by other clients survive an edit.
export type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
export type Weekday = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU'

export const WEEKDAYS: Weekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
// Short weekday name in the UI language; 2024-01-01 was a Monday.
export function weekdayLabel(day: Weekday): string {
  const date = new Date(Date.UTC(2024, 0, 1 + WEEKDAYS.indexOf(day)))
  return new Intl.DateTimeFormat(currentLocale(), { weekday: 'short', timeZone: 'UTC' }).format(date)
}

export interface RepeatRule {
  freq: Frequency
  interval: number
  byday: Weekday[]
  // "YYYY-MM-DD" (the editor only offers a date), or null.
  until: string | null
  count: number | null
  extra: [string, string][]
}

const FREQUENCIES: Frequency[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

export function parseRRule(text: string | null): RepeatRule | null {
  if (!text) return null
  const rule: RepeatRule = {
    freq: 'DAILY',
    interval: 1,
    byday: [],
    until: null,
    count: null,
    extra: [],
  }
  let hasFreq = false
  for (const part of text.replace(/^RRULE:/i, '').split(';')) {
    const [rawKey, value = ''] = part.split('=')
    const key = rawKey.toUpperCase()
    if (key === 'FREQ' && FREQUENCIES.includes(value.toUpperCase() as Frequency)) {
      rule.freq = value.toUpperCase() as Frequency
      hasFreq = true
    } else if (key === 'INTERVAL' && Number(value) > 0) {
      rule.interval = Number(value)
    } else if (
      key === 'BYDAY' &&
      value.split(',').every((d) => WEEKDAYS.includes(d.toUpperCase() as Weekday))
    ) {
      rule.byday = value.split(',').map((d) => d.toUpperCase() as Weekday)
    } else if (key === 'UNTIL' && /^\d{8}/.test(value)) {
      rule.until = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
    } else if (key === 'COUNT' && Number(value) > 0) {
      rule.count = Number(value)
    } else if (key) {
      rule.extra.push([key, value])
    }
  }
  return hasFreq ? rule : null
}

export function formatRRule(rule: RepeatRule | null): string | null {
  if (!rule) return null
  const parts = [`FREQ=${rule.freq}`]
  if (rule.interval > 1) parts.push(`INTERVAL=${rule.interval}`)
  if (rule.byday.length)
    parts.push(`BYDAY=${WEEKDAYS.filter((d) => rule.byday.includes(d)).join(',')}`)
  if (rule.until) parts.push(`UNTIL=${rule.until.replace(/-/g, '')}`)
  else if (rule.count) parts.push(`COUNT=${rule.count}`)
  for (const [key, value] of rule.extra) parts.push(`${key}=${value}`)
  return parts.join(';')
}

const UNIT: Record<Frequency, Intl.NumberFormatOptions['unit']> = {
  DAILY: 'day',
  WEEKLY: 'week',
  MONTHLY: 'month',
  YEARLY: 'year',
}

export function describeRRule(text: string | null): string {
  const rule = parseRRule(text)
  const { t } = i18n.global
  if (!rule) return text ? t('rrule.custom') : t('rrule.never')
  let result =
    rule.interval === 1
      ? t(`rrule.every${rule.freq[0]}${rule.freq.slice(1).toLowerCase()}`)
      : t('rrule.everyN', {
          amount: new Intl.NumberFormat(currentLocale(), {
            style: 'unit',
            unit: UNIT[rule.freq],
            unitDisplay: 'long',
          }).format(rule.interval),
        })
  if (rule.byday.length) {
    const days = WEEKDAYS.filter((d) => rule.byday.includes(d)).map(weekdayLabel).join(', ')
    result = t('rrule.onDays', { base: result, days })
  }
  if (rule.until) result = t('rrule.until', { base: result, date: rule.until })
  else if (rule.count) result = t('rrule.more', { base: result, n: rule.count }, rule.count)
  if (rule.extra.length) result = t('rrule.extra', { base: result })
  return result
}
